import React from 'react';
import { useTranslation } from 'react-i18next';
import styled from 'styled-components';

import { PageTitle } from '../../../components/common/PageTitle/PageTitle';
import * as S from '../Legal.styles';
import usaFlag from "../../../assets/icons/flags/united_states.svg";
import australiaFlag from "../../../assets/icons/flags/australia.svg";
import englandFlag from "../../../assets/icons/flags/united kingdom.svg";
import { BREAKPOINTS } from '../../../styles/themes/constants';

const href = window.location.href;
const noIndex = true;

// Responsive contact cards
const ContactGrid = styled.div`
  display: grid;
  grid-template-columns: repeat(3, 1fr);
  gap: 20px;
  margin: 24px 0 32px 0;
  
  @media (max-width: ${BREAKPOINTS.md}px) {
    grid-template-columns: 1fr;
    gap: 16px;
  }
`;

const ContactCard = styled.div`
  background: #fafafa;
  border-radius: 12px;
  padding: 20px;
  border: 1px solid #f0f0f0;
  text-align: center;
  transition: all 0.2s ease;
  
  &:hover {
    background: #f0f5ff;
    border-color: #91caff;
  }
  
  .flag {
    width: 32px;
    height: 32px;
    margin-bottom: 8px;
  }
  
  .region {
    font-weight: 600;
    color: #1a1a2e;
    margin-bottom: 4px;
    font-size: 16px;
  }
  
  .country {
    color: #666;
    margin-bottom: 12px;
    font-size: 14px;
  }
  
  a {
    color: #1677ff;
    text-decoration: none;
    font-weight: 500;
    
    &:hover {
      text-decoration: underline;
    }
  }
  
  @media (max-width: ${BREAKPOINTS.md}px) {
    padding: 16px;
    
    .region {
      font-size: 15px;
    }
  }
`;

const AddressCard = styled.div`
  background: linear-gradient(135deg, #f0f5ff 0%, #e6f7ff 100%);
  border-radius: 12px;
  padding: 24px;
  margin-top: 24px;
  border: 1px solid #91caff;
  
  h4 {
    color: #0958d9;
    font-weight: 600;
    margin-bottom: 12px;
    font-size: 16px;
  }
  
  p {
    color: #434343;
    margin: 0;
    line-height: 1.6;
  }
  
  @media (max-width: ${BREAKPOINTS.md}px) {
    padding: 20px;
    
    h4 {
      font-size: 15px;
    }
  }
`;

const ContactPage: React.FC = () => {
  const { t } = useTranslation();

  return (
    <>
      <PageTitle 
        title={t('head:aboutUsTitle')} 
        description={t('head:aboutUsDescription')} 
        url={href} 
        noIndex={noIndex} 
      />
      
      <S.H1 level={1}>Contact</S.H1>

      <S.Section>
        <S.H2 level={2}>Contact Sales Team</S.H2>
        <S.Paragraph>
          Please don't contact the sales teams with support questions. Instead, post questions in the{' '}
          <a href="https://forum.omrquiz.app/" rel="alternate">Support Forums</a>.
        </S.Paragraph>

        <ContactGrid>
          <ContactCard>
            <img className="flag" src={usaFlag} alt="USA Flag" />
            <div className="region">America</div>
            <div className="country">USA</div>
            <a href="mailto:sales@omrquiz.com">sales@omrquiz.com</a>
          </ContactCard>
          
          <ContactCard>
            <img className="flag" src={englandFlag} alt="UK Flag" />
            <div className="region">Europe</div>
            <div className="country">UK</div>
            <a href="mailto:sales@omrquiz.com">sales@omrquiz.com</a>
          </ContactCard>
          
          <ContactCard>
            <img className="flag" src={australiaFlag} alt="Australia Flag" />
            <div className="region">Oceania</div>
            <div className="country">Australia</div>
            <a href="mailto:sales@omrquiz.com">sales@omrquiz.com</a>
          </ContactCard>
        </ContactGrid>
      </S.Section>

      <AddressCard>
        <h4>📍 Mailing Address</h4>
        <p>Smallize Pty Ltd, Suite 163, 79 Longueville Road, Lane Cove, NSW, 2066, Australia.</p>
      </AddressCard>
    </>
  );
};

export default ContactPage;